import { WEBSERVICE_CONFIG, HOOK_CONFIG, CREATE_FORM_BY_OPTIONS, FORM_COMPONENTS_MAP } from './webserviceConfig';

/**
 * Gets the translated create form by options
 * @param {Function} t - Translation function
 * @returns {Array} - Array of options with translated text
 */
export function getTranslatedCreateFormByOptions(t) {
    return CREATE_FORM_BY_OPTIONS.map(option => ({
        value: option.value,
        text: t(option.textKey)
    }));
}

/**
 * List of available distributors for selection
 * Excludes hook type as it's handled separately
 */
export const DISTRIBUTORS = Object.entries(WEBSERVICE_CONFIG).map(([value, config]) => ({
    text: config.text,
    value
}));

/**
 * Mapping of fields by type for quick reference
 * Includes both standard webservices and hooks
 */
export const FIELDS_BY_TYPE = {
    ...Object.fromEntries(Object.entries(WEBSERVICE_CONFIG).map(([key, config]) => [key, config.fields])),
    hook: HOOK_CONFIG.fields
};

/**
 * Mapping of form components by type
 * Uses the explicit component mapping to avoid ESLint errors
 */
export const WEBSERVICE_FORMS = Object.fromEntries(
    Object.entries(WEBSERVICE_CONFIG)
        .filter(([_, config]) => config.formComponent && FORM_COMPONENTS_MAP[config.formComponent])
        .map(([key, config]) => [key, FORM_COMPONENTS_MAP[config.formComponent]])
);

/**
 * Generates initial values for a form based on selected type
 * @param {string} type - The webservice type
 * @returns {Object} - The object containing initial values
 */
export function getInitialValues(type) {
    // Base values common to all types
    const base = {
        type: type || '',
        create_form_by: 'rate'
    };

    // Special case for hook
    if (type === 'hook') {
        base.name = '';
        base.end_date = null;
        base.default_provider = 'default';
        return base;
    }

    // If type is configured, initialize specific fields
    const fields = FIELDS_BY_TYPE[type];
    if (fields) {
        fields.forEach(field => {
            base[field] = '';
        });
    }

    return base;
}

/**
 * Generates validators for a form based on selected type
 * @param {string} type - The webservice type
 * @returns {Object} - The object containing validators
 */
export function getValidators(type) {
    const validators = {};

    // Special case for hook
    if (type === 'hook') {
        Object.entries(HOOK_CONFIG.required).forEach(([field, isRequired]) => {
            if (isRequired) {
                validators[field] = (name, value) => (value ? true : 'ticket.common.required');
            }
        });
        return validators;
    }

    // For other types, all fields are required
    const fields = FIELDS_BY_TYPE[type];
    if (fields) {
        fields.forEach(field => {
            validators[field] = (name, value) => (value ? true : 'ticket.common.required');
        });
    }

    return validators;
}
